import { useCallback } from "react";
import { Pressable, type PressableProps } from "react-native";
import { Link, useRouter, type Href } from "expo-router";
import { useTenantId } from "./TenantProvider";
import { getPathTenantId } from "./tenant-utils";

/**
 * True when `href` leads into a tenant other than `selected` — including when
 * none is selected yet, as the safe side. Only a path string is read: an object
 * href carries its tenant as a route param, so it counts as staying.
 */
function leavesTenant(href: Href, selected: string): href is Extract<Href, string> {
  if (typeof href !== "string") return false;
  const target = getPathTenantId(href);
  return target !== null && target !== selected;
}

/** `path` as the browser's own URL, under the `/app` base path a production web build serves from. */
function fullPageUrl(path: string): string {
  const basePath = window.location.pathname === "/app" || window.location.pathname.startsWith("/app/") ? "/app" : "";
  return `${basePath}${path}`;
}

/**
 * A link that loads the page in full when it leads into another tenant, and
 * navigates in-app otherwise.
 *
 * An in-app navigation into another tenant reuses the `tenants/[tenantId]`
 * layout, which leaves the selected tenant — and every query keyed by it — on
 * the tenant being left. A full load selects the new tenant from the URL.
 *
 * The cross-tenant link is a plain anchor with `target="_self"` and no router
 * handler, so nothing can intercept the click. Either way it renders a real
 * anchor, so cmd/middle-click opens it in a new tab. Web-only in effect: a
 * native build has no full page load to fall back on.
 */
export function TenantLink({ href, ...pressable }: Omit<PressableProps, "onPress"> & { readonly href: Href }) {
  const selected = useTenantId();

  if (leavesTenant(href, selected)) {
    // React Native Web renders a View with `href` as an anchor, and writes
    // `hrefAttrs` onto it; a bare `target` prop would be dropped.
    return (
      <Pressable
        accessibilityRole="link"
        {...pressable}
        {...{ href: fullPageUrl(href), hrefAttrs: { target: "_self" } }}
      />
    );
  }
  return (
    <Link href={href} asChild>
      <Pressable accessibilityRole="link" {...pressable} />
    </Link>
  );
}

/**
 * Replaces the current page with `href`: in-app within the selected tenant, by
 * a full page load into another — for the reason given on {@link TenantLink}.
 */
export function useReplaceTenantRoute(): (href: Href) => void {
  const router = useRouter();
  const selected = useTenantId();
  return useCallback(
    (href: Href) => {
      if (leavesTenant(href, selected)) {
        window.location.replace(fullPageUrl(href));
        return;
      }
      router.replace(href);
    },
    [router, selected],
  );
}
